import { useMemo, useState } from 'react';
import { safeHref } from '../../lib/safeUrl';
import { ActionIcon, CopyButton, Group, ScrollArea, Stack, Table, Tabs, Text, Tooltip } from '@mantine/core';
import { Check, Copy, Eye, EyeOff, Network, Server, X } from 'lucide-react';
import { Badge, Button, StatusDot } from '../ui';
import { VMTopology } from './VMTopology';
import { useRangeCredentials } from '../../hooks/useRangeCredentials';
import type { AppCredential, RangeHealthVm, RangeVm, VmCredentials } from '../../api/types';

/**
 * RangeInfoOverlay — ludus2-style slide-down panel over the full-screen
 * console. Two tabs: Machines (the range's VMs with IPs/protocol/status and a
 * "switch console" affordance) and Topology (the network diagram). Does not
 * cover the whole console (max-height ~68vh) so the desktop stays visible.
 *
 * Credentials columns are added in Phase C (per-range OS creds + scenario app
 * creds); until then the Machines tab omits them.
 */
export interface RangeInfoOverlayProps {
  open: boolean;
  onClose: () => void;
  rangeId: string;
  vms: ReadonlyArray<RangeHealthVm>;
  activeVmName: string;
  /** Navigate the full-screen console to another VM. */
  onSelectVm: (vmName: string) => void;
}

export function RangeInfoOverlay({ open, onClose, rangeId, vms, activeVmName, onSelectVm }: RangeInfoOverlayProps) {
  // VMTopology wants the RangeVm shape + a hostname->role map. The health
  // endpoint returns RangeHealthVm, so adapt.
  const topoVms: ReadonlyArray<RangeVm> = useMemo(
    () =>
      vms.map((v) => ({
        hostname: v.vmName || v.hostname,
        status: (v.state as RangeVm['status']) ?? 'pending',
        cpu: 0,
        memory: 0,
        agentConnected: v.ssmStatus === 'Online',
      })),
    [vms],
  );
  const rolesByHostname = useMemo(() => {
    const m: Record<string, string> = {};
    for (const v of vms) m[v.vmName || v.hostname] = v.role;
    return m;
  }, [vms]);

  // Phase C: credentials (scenario app creds + OS login), RBAC-filtered server
  // side. Only fetched while the overlay is open.
  const credsQ = useRangeCredentials(rangeId, open);
  const credsByVm = useMemo(() => {
    const m: Record<string, VmCredentials> = {};
    for (const v of credsQ.data?.vms ?? []) m[v.vmName] = v;
    return m;
  }, [credsQ.data]);

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10,
        transform: open ? 'translateY(0)' : 'translateY(-110%)',
        transition: 'transform 200ms ease-out',
        maxHeight: '68vh',
        background: 'rgba(17,17,19,0.96)',
        backdropFilter: 'blur(8px)',
        borderBottom: '1px solid #2a2a2d',
        borderRadius: '0 0 10px 10px',
        boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
      }}
    >
      <Tabs defaultValue="machines" variant="default">
        <Group justify="space-between" px="md" pt="xs">
          <Tabs.List style={{ border: 'none' }}>
            <Tabs.Tab value="machines" leftSection={<Server size={14} />}>
              Machines
            </Tabs.Tab>
            <Tabs.Tab value="topology" leftSection={<Network size={14} />}>
              Topology
            </Tabs.Tab>
          </Tabs.List>
          <Button variant="ghost" size="xs" leftIcon={<X size={14} />} onClick={onClose}>
            Close
          </Button>
        </Group>

        <Tabs.Panel value="machines" p="md">
          <ScrollArea.Autosize mah="56vh">
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>VM</Table.Th>
                  <Table.Th>Role</Table.Th>
                  <Table.Th>Private IP</Table.Th>
                  <Table.Th>Proto</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Credentials</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {vms.map((vm) => {
                  const name = vm.vmName || vm.hostname;
                  const isActive = name === activeVmName;
                  return (
                    <Table.Tr key={vm.instanceId}>
                      <Table.Td>
                        <Group gap={6}>
                          <StatusDot state={vm.state === 'running' ? 'running' : 'idle'} size="sm" />
                          <Text ff="JetBrains Mono" size="sm" c="white">
                            {name}
                          </Text>
                          {isActive && <Badge variant="info" size="xs">current</Badge>}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed" ff="JetBrains Mono">
                          {vm.role}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" ff="JetBrains Mono">
                          {vm.privateIp || '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="neutral" size="xs">
                          {(vm.consoleProtocol || '').toUpperCase() || '—'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {vm.state}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <CredentialCell vm={credsByVm[name]} loading={credsQ.isPending} />
                      </Table.Td>
                      <Table.Td>
                        <Button
                          variant="ghost"
                          size="xs"
                          disabled={isActive || vm.state !== 'running'}
                          onClick={() => onSelectVm(name)}
                        >
                          Console
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>
        </Tabs.Panel>

        <Tabs.Panel value="topology" p="md">
          <Stack gap="xs">
            {topoVms.length === 0 ? (
              <Text size="sm" c="dimmed">
                No VMs to display.
              </Text>
            ) : (
              <VMTopology vms={topoVms} rolesByHostname={rolesByHostname} />
            )}
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </div>
  );
}

function SecretField({ value }: { value: string }) {
  const [shown, setShown] = useState(false);
  return (
    <Group gap={2} wrap="nowrap">
      <Text size="xs" ff="JetBrains Mono" c="white">
        {shown ? value : '••••••••'}
      </Text>
      <ActionIcon variant="subtle" size="xs" color="gray" onClick={() => setShown((s) => !s)} aria-label="reveal">
        {shown ? <EyeOff size={12} /> : <Eye size={12} />}
      </ActionIcon>
      <CopyButton value={value}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow>
            <ActionIcon variant="subtle" size="xs" color={copied ? 'green' : 'gray'} onClick={copy} aria-label="copy">
              {copied ? <Check size={12} /> : <Copy size={12} />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </Group>
  );
}

function CredentialCell({ vm, loading }: { vm: VmCredentials | undefined; loading: boolean }) {
  if (loading) return <Text size="xs" c="dimmed">…</Text>;
  const entries: AppCredential[] = [];
  if (vm?.os) {
    entries.push({ label: 'OS login', type: 'os-login', username: vm.os.username, password: vm.os.password });
  }
  if (vm) entries.push(...vm.applications);
  if (entries.length === 0) {
    return <Text size="xs" c="dimmed">—</Text>;
  }
  return (
    <Stack gap={6} style={{ minWidth: 240 }}>
      {entries.map((c, i) => (
        <Stack key={`${c.label}-${i}`} gap={0}>
          <Text size="xs" c="dimmed" fw={600}>
            {c.label}
          </Text>
          <Group gap={6} wrap="nowrap">
            <Text size="xs" ff="JetBrains Mono" c="white">
              {c.username}
            </Text>
            <SecretField value={c.password} />
          </Group>
          {c.url && (
            <Text size="xs" c="cyan" component="a" href={safeHref(c.url)} target="_blank" rel="noreferrer noopener">
              {c.url}
            </Text>
          )}
          {c.notes && (
            <Text size="xs" c="dimmed">
              {c.notes}
            </Text>
          )}
        </Stack>
      ))}
    </Stack>
  );
}
